<template>
  <div class="Step3MedicalSituationForm">
    <h2 class="gl_form-title small">
      {{ t(`hiring.common.medical-situation-form.legend.${person}`) }}
    </h2>

    <div
      v-for="(question, key) in medicalSituation"
      :key="key"
      class="question"
    >
      <h3 class="gl_heading secondary">
        {{ t(`hiring.common.medical-situation-form.${key}.title.${person}`) }}
      </h3>

      <YesNoComponent
        :id="`medical-condition-${key}`"
        :rules="[yesNoRequired]"
        v-model="question.checked"
      />

      <p
        v-if="te(`hiring.common.medical-situation-form.${key}.subtitle`)"
        class="subtitle"
      >
        {{ t(`hiring.common.medical-situation-form.${key}.subtitle`) }}
      </p>

      <template v-if="question.checked">
        <p class="label">
          {{ t('hiring.common.medical-situation-form.select-insured') }}
        </p>
        <CheckButtonGroupComponent
          :id="`medical-conditions-insured-${key}`"
          :modelValue="question.insured"
          @update:modelValue="setInsured(key, $event)"
          :options="insuredOptions"
          :rules="[required]"
        />

        <InsuranceEntryForm
          v-for="insuredIndex in question.insured"
          :key="String(insuredIndex)"
          :id="String(insuredIndex)"
          :title="insuredOptions[Number(insuredIndex)].label"
          :entries="question.values[Number(insuredIndex)]"
          :labelPrefix="`hiring.common.medical-situation-form.${key}`"
          :labelSuffix="person"
          :role="selectedRole"
          @update:modelValue="question.values[Number(insuredIndex)] = $event"
        />
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { ref, computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import YesNoComponent from '@/modules/core/components/formFields/YesNoComponent.vue'
  import CheckButtonGroupComponent from '@/modules/core/components/ui/CheckButtonGroupComponent.vue'
  import InsuranceEntryForm from '@/modules/health/hiring/components/InsuranceEntryForm.vue'
  import { format } from '@/utils/date.utils'
  import HealthHiringInsured from '@/modules/health/hiring/models/APP/HealthHiringInsured'
  import MedicalSituationAdmission from '@/modules/health/hiring/models/APP/MedicalSituationAdmission'
  import HealthHiringInsuredMedicalSituation from '@/modules/health/hiring/models/APP/HealthHiringInsuredMedicalSituation'
  import { UserRoleEnum } from '@/modules/core/models/enums'

  interface HealthSituationRenderData {
    checked: boolean | null
    insured: string[]
    values: MedicalSituationAdmission[][]
  }

  const { t, te } = useI18n()
  const { required, yesNoRequired } = useRules()

  defineExpose({ triggerSubmit })

  interface PropsInterface {
    nextForm: String[]
    answers: Record<string, any>
    configurations: Record<string, any>
  }
  const props = withDefaults(defineProps<PropsInterface>(), {})
  const selectedRole = ref('')

  const insuredOptions = ref(
    props.answers.insureds.map((item: HealthHiringInsured, i: number) => ({
      label: `${item.userDetails.name} (${
        item.userDetails.birthDate ? format(item.userDetails.birthDate) : ''
      })`,
      value: String(i)
    }))
  )
  const insureds = ref<HealthHiringInsured[]>(props.answers.insureds)

  const medicalSituation = ref<Record<string, HealthSituationRenderData>>({
    medical: loadMedicalSituation('medical'),
    surgical: loadMedicalSituation('surgical'),
    cancer: loadMedicalSituation('cancer'),
    symptoms: loadMedicalSituation('symptoms')
  })

  const person = computed(() => {
    const holderRole = props.answers.holder.role
    const moreThanOne = props.answers.insureds.length > 1

    if (!moreThanOne) {
      if (holderRole === UserRoleEnum.HOLDER_INSURED) {
        return 'me'
      }

      return 'not-me'
    }

    if (holderRole === UserRoleEnum.HOLDER_INSURED) {
      return 'holder'
    }

    return 'not-holder'
  })

  function setInsured(key: keyof typeof medicalSituation.value, newInsured: string[]) {
    const previous = medicalSituation.value[key].insured
    medicalSituation.value[key].insured = newInsured
    selectedRole.value = props.answers.insureds[Number(newInsured)].role
    const removedInsured = previous.filter((i) => !newInsured.includes(i))
    if (removedInsured.length) {
      medicalSituation.value[key].values[Number(removedInsured)] = []
    }
  }

  function loadMedicalSituation(
    key: keyof Omit<HealthHiringInsuredMedicalSituation, 'filled'>
  ): HealthSituationRenderData {
    const filled = insureds.value.some((i) => i.medicalSituation.filled) || null
    const admissions = insureds.value.map((i) => i.medicalSituation[key]) ?? []
    const insuredWithPreviousData: string[] = admissions
      .map((c, i) => (c.length ? String(i) : ''))
      .filter((c) => c !== '')

    return {
      checked: filled ? insuredWithPreviousData.length > 0 : filled,
      insured: insuredWithPreviousData,
      values: admissions as MedicalSituationAdmission[][]
    }
  }

  function getValuesToSave(key: string, insuredIndex: number) {
    const { checked, values } = medicalSituation.value[key as keyof typeof medicalSituation.value]
    if (!checked) return null

    return values[insuredIndex]
  }

  function triggerSubmit() {
    const page = props.nextForm[0]

    const insuredData = insureds.value.map((item, i) => {
      const mS = item.medicalSituation
      mS.cancer = getValuesToSave('cancer', i) as MedicalSituationAdmission[]
      mS.medical = getValuesToSave('medical', i) as MedicalSituationAdmission[]
      mS.surgical = getValuesToSave('surgical', i) as MedicalSituationAdmission[]
      mS.symptoms = getValuesToSave('symptoms', i) as MedicalSituationAdmission[]
      mS.filled = true

      return item
    })

    const answers = { insureds: insuredData }
    return { page, answers }
  }
</script>

<style lang="scss" scoped>
  .Step3MedicalSituationForm {
    .question + .question {
      .gl_heading.secondary {
        margin-top: $spacing-96;
      }
    }

    .subtitle {
      font-size: $font-size-s;
      color: $color-neutral;
      margin-bottom: $spacing-m;
    }

    .label {
      margin-top: $spacing-2xl;
      color: $color-neutral-dark;
      font-size: $font-size-m;
    }
  }
</style>
